import { useMemo, useState } from 'react';
import { Armchair, Search } from 'lucide-react';
import type { GuestTableOption } from '@peles/shared';
import { Popover } from '../../common/Popover';
import { useEventGuestTables } from '../../../hooks/useEventGuests';

export interface SeatPickerProps {
  eventId: string;
  /** The table the guest (or selection) sits at now. */
  tableNumber: number | null;
  /** People who would arrive — used to say a table will not hold them. */
  partySize?: number;
  /** Chosen a table, or `null` for "no table". */
  onChoose: (tableNumber: number | null) => void;
  /** Renders the trigger's text; defaults to "T4" / "Seat". */
  label?: string;
  disabled?: boolean;
  /** How the trigger looks: a small chip in a row, a full-width field in a form. */
  variant?: 'chip' | 'field';
}

/** "7/10", or "7" when the table has no seat count on a plan. */
export function occupancyText(table: GuestTableOption): string {
  return table.capacity != null
    ? `${table.seatedPeople}/${table.capacity}`
    : String(table.seatedPeople);
}

function freeSeats(table: GuestTableOption): number | null {
  return table.capacity != null ? table.capacity - table.seatedPeople : null;
}

/**
 * Choose a table from the event's tables, each shown with how full it is
 * ("T4 · 7/10"). A table that cannot take the party is shown as full — never
 * as free — though it can still be chosen; the server then asks whether to
 * seat them anyway.
 */
export function SeatPicker({
  eventId,
  tableNumber,
  partySize = 1,
  onChoose,
  label,
  disabled,
  variant = 'chip',
}: SeatPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { data: tables = [], isLoading } = useEventGuestTables(eventId, open);

  const shown = useMemo(() => {
    const q = query.trim().replace(/^t(able)?\s*/i, '');
    return q ? tables.filter((t) => String(t.tableNumber).startsWith(q)) : tables;
  }, [tables, query]);

  const text = label ?? (tableNumber != null ? `T${tableNumber}` : 'Seat');
  const triggerClass =
    variant === 'field'
      ? 'w-full flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm text-left bg-white'
      : `inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
          tableNumber != null
            ? 'border-primary-200 bg-primary-50 text-primary-700'
            : 'border-dashed border-gray-300 text-gray-500'
        }`;

  return (
    <Popover
      ariaLabel="Choose a table"
      panelClassName="w-72"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          onClick={() => {
            setOpen(true);
            props.onClick();
          }}
          disabled={disabled}
          className={`${triggerClass} disabled:opacity-60 min-h-[32px]`}
        >
          <Armchair size={14} aria-hidden />
          <span className={variant === 'field' ? 'flex-1' : ''}>{text}</span>
        </button>
      )}
    >
      {(close) => (
        <div>
          <div className="relative mb-2">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Table number"
              aria-label="Find a table"
              className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto -mx-1" role="listbox" aria-label="Tables">
            {tableNumber != null && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    close();
                    onChoose(null);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  No table
                </button>
              </li>
            )}
            {isLoading && <li className="px-3 py-2 text-sm text-gray-500">Loading tables…</li>}
            {!isLoading && shown.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">
                {tables.length === 0 ? 'No tables on the floor plan yet' : 'No table matches'}
              </li>
            )}
            {shown.map((table) => {
              const free = freeSeats(table);
              const here = table.tableNumber === tableNumber;
              const full = free != null && free - (here ? 0 : partySize) < 0;
              return (
                <li key={table.tableNumber}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={here}
                    onClick={() => {
                      close();
                      if (!here) onChoose(table.tableNumber);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 ${
                      here ? 'font-semibold text-primary-700' : 'text-gray-800'
                    }`}
                  >
                    <span>Table {table.tableNumber}</span>
                    <span
                      className={`text-xs ${full ? 'text-red-600 font-medium' : 'text-gray-500'}`}
                    >
                      {occupancyText(table)}
                      {full ? ' · full' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Popover>
  );
}
